import { __asyncDelegator, __asyncGenerator, __asyncValues, __await, __awaiter } from "tslib";
import { Backoff, Event, Status, assert, errors_pb, react_pb, retryForever, stateIdToRef, } from "@reboot-dev/reboot-api";
export class Deferred {
    constructor(deferredFn) {
        this._deferredFn = deferredFn;
        this._resolve = (_result) => { };
        this._reject = (_e) => { };
        this._promise = new Promise((resolve, reject) => {
            this._resolve = resolve;
            this._reject = reject;
        });
    }
    start() {
        this._deferredFn()
            .then((result) => this._resolve(result))
            .catch((e) => this._reject(e));
    }
    get promise() {
        return this._promise;
    }
}
export function httpCall(_a) {
    return __awaiter(this, arguments, void 0, function* ({ url, method, stateRef, requestType, responseType, abortedType, request, idempotencyKey, options, bearerToken, }) {
        if (!(request instanceof requestType)) {
            throw new TypeError("'request' is not of type 'requestType'");
        }
        const headers = new Headers();
        headers.set("Content-Type", "application/json");
        headers.set("x-reboot-idempotency-key", idempotencyKey);
        if (bearerToken !== undefined) {
            const token = yield bearerToken();
            if (token !== undefined) {
                headers.set("Authorization", `Bearer ${token}`);
            }
        }
        // Fetch with retry, using a backoff, i.e., if we get disconnected.
        const { response, aborted } = yield (() => __awaiter(this, void 0, void 0, function* () {
            var _b;
            const backoff = new Backoff();
            while (true) {
                try {
                    // Invariant here is that we use the '/package.service.method' path and
                    // HTTP 'POST' method (we need 'POST' because we send an HTTP body).
                    //
                    // See also 'reboot/helpers.py'.
                    const response = yield guardedFetch(new Request(`${url}/__/reboot/rpc/${stateRef}/${method}`, {
                        method: "POST",
                        headers,
                        // Tell browser to pass cookies, even cross-origin.
                        // TODO: Enable when the backend supports CORS.
                        // credentials: "include",
                        body: request.toJsonString(),
                    }), options);
                    // Since it is a 'fetch', we could get an 'Unavailable', but
                    // it won't throw an exception, so we have to check the
                    // response status code for 'Unavailable' and retry it.
                    if (response.status === 503) {
                        if (response.headers.get("content-type") === "application/json") {
                            const status = Status.fromJson(yield response.json());
                            const aborted = abortedType.fromStatus(status);
                            // Log the error later in the 'catch' block.
                            throw aborted;
                        }
                        else {
                            const aborted = new abortedType(new errors_pb.Unknown(), {
                                message: `Unknown error with HTTP status ${response.status}`,
                            });
                            throw aborted;
                        }
                    }
                    return { response };
                }
                catch (e) {
                    if ((_b = options === null || options === void 0 ? void 0 : options.signal) === null || _b === void 0 ? void 0 : _b.aborted) {
                        const aborted = new abortedType(new errors_pb.Aborted(), {
                            message: e instanceof Error
                                ? `${e}`
                                : `Unknown error: ${JSON.stringify(e)}`,
                        });
                        return { aborted };
                    }
                    else if (e instanceof Error) {
                        console.error(e);
                    }
                    else {
                        console.error(`Unknown error: ${JSON.stringify(e)}`);
                    }
                }
                console.warn(`Retrying call to '${method}' after backoff ...`);
                yield backoff.wait();
            }
        }))();
        if (!response) {
            if (aborted)
                throw aborted;
            throw new Error("Unexpected: response and aborted both undefined");
        }
        if (!response.ok) {
            if (response.headers.get("content-type") === "application/json") {
                const status = Status.fromJson(yield response.json());
                const aborted = abortedType.fromStatus(status);
                console.warn(`'${method}' aborted with ${aborted.message}`);
                throw aborted;
            }
            else {
                const aborted = new abortedType(new errors_pb.Unknown(), {
                    message: `Unknown error with HTTP status ${response.status}`,
                });
                throw aborted;
            }
        }
        else {
            return responseType.fromJson(yield response.json());
        }
    });
}
export function reactively({ url, state, method, id, requestType, responseType, request, signal, bearerToken, }) {
    if (request !== undefined) {
        if (!(request instanceof requestType)) {
            throw new TypeError("'request' is not of type 'requestType'");
        }
    }
    // Track whether or not we've got the first request or are still
    // waiting for it.
    const firstRequest = new Event();
    if (request !== undefined) {
        firstRequest.set();
    }
    // We need a separate `AbortController` so we can abort `responses`
    // if/when we get a new request via `setRequest`.
    let responsesAbortController = new AbortController();
    if (signal !== undefined) {
        signal.addEventListener("abort", () => {
            responsesAbortController.abort();
        });
    }
    const setRequest = (partialRequest) => {
        const isFirstRequest = request === undefined;
        request =
            partialRequest instanceof requestType
                ? partialRequest
                : new requestType(partialRequest);
        if (isFirstRequest) {
            firstRequest.set();
        }
        else {
            // Otherwise abort current `responses` so that we'll restart
            // with the new request.
            responsesAbortController.abort();
            responsesAbortController = new AbortController();
        }
    };
    function responses() {
        return __asyncGenerator(this, arguments, function* responses_1() {
            var _a, e_1, _b, _c;
            // Wait for either the first request or an abort.
            yield __await(Promise.race([
                firstRequest.wait(),
                new Promise((resolve) => {
                    if (signal !== undefined) {
                        signal.addEventListener("abort", () => {
                            resolve();
                        });
                    }
                }),
            ]));
            const backoff = new Backoff();
            assert(request !== undefined);
            while (signal === undefined || !signal.aborted) {
                try {
                    const queryRequest = new react_pb.QueryRequest(Object.assign({ method, request: request.toBinary() }, ((bearerToken !== undefined && {
                        bearerToken: yield __await(bearerToken()),
                    }) ||
                        {})));
                    const stateRef = stateIdToRef(state, id);
                    const queryResponses = reactiveReader({
                        endpoint: `${url}/__/reboot/rpc/${stateRef}`,
                        request: queryRequest,
                        signal: responsesAbortController.signal,
                    });
                    try {
                        for (var _d = true, queryResponses_1 = (e_1 = void 0, __asyncValues(queryResponses)), queryResponses_1_1; queryResponses_1_1 = yield __await(queryResponses_1.next()), _a = queryResponses_1_1.done, !_a; _d = true) {
                            _c = queryResponses_1_1.value;
                            _d = false;
                            const queryResponse = _c;
                            if (queryResponse.responseOrStatus.case === "response") {
                                const response = responseType.fromBinary(queryResponse.responseOrStatus.value);
                                yield yield __await(response);
                            }
                        }
                    }
                    catch (e_1_1) { e_1 = { error: e_1_1 }; }
                    finally {
                        try {
                            if (!_d && !_a && (_b = queryResponses_1.return)) yield __await(_b.call(queryResponses_1));
                        }
                        finally { if (e_1) throw e_1.error; }
                    }
                }
                catch (e) {
                    console.warn(`Retrying call to '${method}' after backoff ...`);
                    yield __await(backoff.wait());
                    continue;
                }
            }
        });
    }
    return [responses(), setRequest];
}
export function reactiveReader(_a) {
    return __asyncGenerator(this, arguments, function* reactiveReader_1({ endpoint, request, signal, }) {
        var _b, e_2, _c, _d;
        const url = new URL(`${endpoint}/rbt.v1alpha1.React/Query`);
        if (url.protocol === "https:") {
            const headers = new Headers();
            if (request.bearerToken !== undefined) {
                headers.set("Authorization", `Bearer ${request.bearerToken}`);
            }
            yield __await(yield* __asyncDelegator(__asyncValues(grpcServerStream({
                endpoint: url.toString(),
                method: "POST",
                headers,
                request,
                responseType: react_pb.QueryResponse,
                signal,
            }))));
        }
        else {
            // TODO: while technically we could `await
            // grpcWebsocketServerStream(...)` doing so will leak websockets
            // because it is possible that `signal` will be set to a different
            // reference and not get triggered. We should fix this by making
            // sure that `signal` is immutable.
            const responses = grpcWebsocketServerStream({
                url,
                request,
                // We use an "empty" QueryRequest as a heartbeat on the channel.
                heartbeatRequest: new react_pb.QueryRequest(),
                responseType: react_pb.QueryResponse,
                signal,
            });
            try {
                for (var _f = true, responses_2 = __asyncValues(responses), responses_2_1; responses_2_1 = yield __await(responses_2.next()), _b = responses_2_1.done, !_b; _f = true) {
                    _d = responses_2_1.value;
                    _f = false;
                    const response = _d;
                    if (response.responseOrStatus.case == "status") {
                        responses.return();
                        throw Status.fromJsonString(response.responseOrStatus.value);
                    }
                    yield yield __await(response);
                }
            }
            catch (e_2_1) { e_2 = { error: e_2_1 }; }
            finally {
                try {
                    if (!_f && !_b && (_c = responses_2.return)) yield __await(_c.call(responses_2));
                }
                finally { if (e_2) throw e_2.error; }
            }
        }
    });
}
// While it is hard to find specific documentation for concrete
// websocket limits, it looks like ~200 is a safe number to bet on
// (Firefox seems to have this limit, while Chrome is at 255, and
// Safari is unknown).
const WEBSOCKET_LIMIT = 
// TODO: figure out a better way to detect that this is Chrome.
// @ts-ignore: `window.navigator.vendor` is deprecated.
// Check that window is defined. We might be on the server if in e.g. Next.js.
typeof window !== "undefined" && window.navigator.vendor === "Google Inc."
    ? 255
    : 200;
export class WebSockets {
    constructor() {
        this.count = 0;
        this.connectionAbortControllers = {};
    }
    // Helper for setting up a long-lived HTTP/2 connection that
    // websockets can use as defined by RFC 8441
    // (https://datatracker.ietf.org/doc/html/rfc8441) and implemented
    // by all of the top browsers.
    //
    connect(endpoint, stateRef) {
        // HTTP/2 requires TLS so if we're not connecting with `https:` then we're
        // not using HTTP/2 so there is no need to create this long-lived connection.
        const url = new URL(endpoint);
        if (url.protocol !== "https:") {
            return;
        }
        const abortController = new AbortController();
        this.connectionAbortControllers[endpoint + stateRef] = abortController;
        retryForever(() => __awaiter(this, void 0, void 0, function* () {
            const headers = new Headers();
            headers.set("Content-Type", "application/json");
            headers.append("Connection", "keep-alive");
            // NOTE: we use `fetch()` not `guardedFetch()`
            // because if there are only mutations than a disconnect will
            // cause us to show a warning when using `rbt dev` but we won't
            // ever remove the warning because the fetch to
            // `WebSocketsConnection` waits indefinitely.
            yield fetch(new Request(`${endpoint}/__/reboot/rpc/${stateRef}/rbt.v1alpha1.React/WebSocketsConnection`, {
                method: "POST",
                headers,
                body: new react_pb.WebSocketsConnectionRequest().toJsonString(),
            }), { signal: abortController.signal }).catch((error) => {
                // Retry forever unless we were aborted.
                if (!abortController.signal.aborted) {
                    throw error;
                }
            });
        }));
    }
    disconnect(endpoint, stateRef) {
        // No need to do anything if we never made a connection in `connect()` because
        // we're not using TLS.
        const url = new URL(endpoint);
        if (url.protocol !== "https:") {
            return;
        }
        this.connectionAbortControllers[endpoint + stateRef].abort();
    }
    create(url) {
        // NOTE: if we're using TLS then all of our websockets should use
        // the existing HTTP/2 connection set up by calling
        // `rbt.v1alpha1.React/WebSocketsConnection` in `connect()`.
        if (url.protocol === "wss:") {
            return new WebSocket(url);
        }
        const websocket = new WebSocket(url);
        this.count += 1;
        websocket.addEventListener("error", () => {
            if (this.count > WEBSOCKET_LIMIT) {
                console.warn(`You have over ${WEBSOCKET_LIMIT} websockets, which is more ` +
                    "than supported on some browsers. This may be the reason one " +
                    "of the websockets we created had an error, and thus some your " +
                    "calls (specifically reactive readers or mutations) will never " +
                    "make it to your Reboot application (even though we keep retrying). " +
                    "You can solve this by using HTTP/2 which allows an unlimited " +
                    "number of concurrent streams. Reboot uses HTTP/2 by default " +
                    "when you use TLS. You should definitely use TLS when you deploy " +
                    "your application in the cloud, but you can also use TLS when " +
                    "running `rbt dev`, please see the documentation for how to do so.");
            }
        });
        websocket.addEventListener("close", () => {
            this.count -= 1;
        });
        return websocket;
    }
}
export const websockets = new WebSockets();
// Helper async generator that conforms to the gRPC JSON transcoder
// protocol, i.e., yields JSON responses or throws a `Status` if
// the request failed or throws an `Error` if another error occurred.
export function grpcServerStream(_a) {
    return __asyncGenerator(this, arguments, function* grpcServerStream_1({ endpoint, method, headers, request, responseType, signal, }) {
        // Ensure we're speaking JSON.
        headers.set("Content-Type", "application/json");
        // Keep the connection alive as long as server is sending data.
        headers.append("Connection", "keep-alive");
        const response = yield __await(guardedFetch(new Request(endpoint, {
            method,
            headers,
            body: request.toJsonString(),
        }), { signal }));
        if (!response.ok) {
            if (response.headers.get("content-type") === "application/json") {
                throw Status.fromJson(yield __await(response.json()));
            }
            throw new Error(`Unknown error with HTTP status ${response.status}`);
        }
        if (response.body === null) {
            throw new Error("Unable to read body of response");
        }
        const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
        if (reader === undefined) {
            throw new Error("Not able to instantiate reader on response body");
        }
        let accumulated = "";
        while (true) {
            const { value, done } = yield __await(reader.read());
            if (done) {
                break;
            }
            accumulated += value.trim();
            if (accumulated.startsWith(",")) {
                accumulated = accumulated.substring(1);
            }
            if (!accumulated.startsWith("[")) {
                accumulated = "[" + accumulated;
            }
            if (!accumulated.endsWith("]")) {
                accumulated = accumulated + "]";
            }
            try {
                // We may receive more than one response, and we need to yield
                // each one separately.
                for (const json of JSON.parse(accumulated)) {
                    yield yield __await(responseType.fromJson(json));
                }
                accumulated = "";
            }
            catch (e) {
                // It's possible that we've only received part of the data
                // necessary to parse and more is on the way. In that case, we
                // drop the ']' that we added to the end and loop back up to
                // keep receiving.
                if (e instanceof SyntaxError) {
                    accumulated = accumulated.substring(0, accumulated.length - 1);
                    continue;
                }
                else {
                    throw e;
                }
            }
        }
    });
}
// Helper async generator that yields responses of type `ResponseType`
// or a `Status` if the request failed.
//
// TODO(benh): support `ResponseOrAborted` rather than `ResponsOrStatus`.
export function grpcInfiniteStream(_a) {
    return __asyncGenerator(this, arguments, function* grpcInfiniteStream_1({ endpoint, method, headers, request, responseType, signal, }) {
        var _b, e_3, _c, _d;
        const backoff = new Backoff();
        while (true) {
            try {
                const responses = yield __await(grpcServerStream({
                    endpoint,
                    method,
                    headers,
                    request,
                    responseType,
                    signal,
                }));
                try {
                    for (var _f = true, responses_3 = (e_3 = void 0, __asyncValues(responses)), responses_3_1; responses_3_1 = yield __await(responses_3.next()), _b = responses_3_1.done, !_b; _f = true) {
                        _d = responses_3_1.value;
                        _f = false;
                        const response = _d;
                        yield yield __await({ response });
                    }
                }
                catch (e_3_1) { e_3 = { error: e_3_1 }; }
                finally {
                    try {
                        if (!_f && !_b && (_c = responses_3.return)) yield __await(_c.call(responses_3));
                    }
                    finally { if (e_3) throw e_3.error; }
                }
                throw new Error("Not expecting infinite stream to ever be done");
            }
            catch (e) {
                if (signal.aborted) {
                    // TODO(benh): propagate the exception or just end the stream
                    // since we aborted?
                    return yield __await(void 0);
                }
                else if (e instanceof Status) {
                    yield yield __await({ status: e });
                }
                else if (e instanceof Error) {
                    console.error(e.message);
                }
                else {
                    console.error(`Unknown error: ${JSON.stringify(e)}`);
                }
            }
            console.warn(`Retrying call to '${method}' after backoff ...`);
            yield __await(backoff.wait());
        }
    });
}
export function grpcWebsocketServerStream(_a) {
    return __asyncGenerator(this, arguments, function* grpcWebsocketServerStream_1({ url, request, heartbeatRequest, responseType, signal, }) {
        url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
        const websocket = websockets.create(url);
        signal.addEventListener("abort", () => {
            if (websocket.readyState !== WebSocket.CLOSED) {
                websocket.close();
            }
        });
        websocket.binaryType = "arraybuffer";
        websocket.onopen = () => {
            websocket.send(request.toBinary());
        };
        const closed = new Promise((resolve, reject) => {
            function sendHeartbeat() {
                if (websocket.readyState === WebSocket.OPEN) {
                    // To detect dead websocket connections, we need to have outbound data in
                    // our buffer (see https://github.com/reboot-dev/mono/issues/4237).
                    //
                    // There is no need for error handling: if this data cannot be sent, then
                    // the socket will be closed automatically.
                    websocket.send(heartbeatRequest.toBinary());
                }
            }
            // This interval matches the setting for non-browser clients in
            // `reboot/settings.py`.
            const heartbeatId = setInterval(sendHeartbeat, 5000);
            websocket.addEventListener("close", () => {
                // Closing the websocket happens both immediately after errors, and when
                // a request is simply done. We can't assume that the connection is
                // healthy or unhealthy; leave any warnings as they are.
                clearInterval(heartbeatId);
                resolve();
            });
            websocket.addEventListener("error", (event) => {
                // We weren't able to reach the server. Show a helpful error message to
                // developers if this persists.
                maybeScheduleLocalhostWarning({
                    id: "grpcWebsocketServerStream",
                    url: url.href,
                });
                clearInterval(heartbeatId);
                reject(new Error(`Unexpected error on websocket: ${JSON.stringify(event)}`));
            });
        });
        try {
            do {
                // Wait for either `closed` to resolve or reject. If the latter,
                // it'll propagate the exception, if the former, we'll drop out of
                // the loop and return to the caller.
                const event = yield __await(Promise.race([
                    closed,
                    new Promise((resolve) => {
                        websocket.addEventListener("message", (event) => {
                            resolve(event);
                        }, 
                        // Only get one message at a time!
                        { once: true });
                    }),
                ]));
                if (event instanceof MessageEvent) {
                    // Receiving a message demonstrates that the websocket is
                    // connected; if we had a connection warning displayed we can
                    // remove it.
                    removeWarning({ id: "grpcWebsocketServerStream" });
                    yield yield __await(responseType.fromBinary(new Uint8Array(event.data)));
                }
                // If not `event instanceof Event` then `closed` must have
                // resolved in which case we'll fall out of this loop and return
                // to the caller.
            } while (websocket.readyState === WebSocket.OPEN);
        }
        finally {
            // Handle the case where the async generator is is not exhausted
            // and we want to make sure we close the websocket.
            if (websocket.readyState !== WebSocket.CLOSED) {
                websocket.close();
            }
        }
    });
}
// A map that has the host as the key and the number of outstanding fetches
// for that host as the value.
const totalOutstandingFetches = new Map();
// A map that has the host as the key and the number of long-running
// outstanding fetches for that host as the value.
const totalLongRunningOutstandingFetches = new Map();
// The time in milliseconds that a fetch should be considered long running.
const fetchTimeout = 3000;
// The threshold for the total number of long-running outstanding fetches that
// the user can have before triggering an alert.
const totalLongRunningOutstandingFetchesThreshold = 4;
// A set of hosts that we have already warned the user about.
// We will wait until the number of long-running outstanding fetches for a host
// returns below the threshold before warning the user again.
const haveConsoleWarned = new Set();
function trackFetch(url) {
    // We will not track fetches to HTTPS endpoints.
    if (url.protocol === "https:") {
        return { completeFetch: () => { } };
    }
    const status = { isLongRunning: false };
    const host = url.host;
    const timeoutId = setTimeout(() => {
        // If the timeout is reached, we will consider the fetch long running.
        status.isLongRunning = true;
        const fetches = totalLongRunningOutstandingFetches.get(host) || 0;
        // Increment the total number of long-running outstanding fetches to the host.
        totalLongRunningOutstandingFetches.set(host, fetches + 1);
        if (fetches + 1 >= totalLongRunningOutstandingFetchesThreshold &&
            !haveConsoleWarned.has(host)) {
            console.warn(`Reboot is currently making ${totalOutstandingFetches.get(host)} outstanding ` +
                `HTTP fetches calling your reader methods, of which it appears as though ` +
                `${totalLongRunningOutstandingFetches.get(host)} are either long-running or ` +
                `queued because your browser limits the total number of simultaneous connections ` +
                `to the same host (${host}). You can solve this by ensuring you use HTTP/2 which ` +
                `requires TLS. See https://docs.reboot.dev/tools/cli/#bring-your-own-certificate-with-rbt-dev-run ` +
                `for details on how to use TLS with 'rbt dev' (which works similarly for 'rbt serve').`);
            haveConsoleWarned.add(host);
        }
    }, fetchTimeout);
    // Increment the total number of outstanding fetches to the host.
    totalOutstandingFetches.set(host, (totalOutstandingFetches.get(host) || 0) + 1);
    return {
        // Called after a fetch is complete to properly update the global maps.
        completeFetch: () => {
            clearTimeout(timeoutId);
            if (status.isLongRunning) {
                const fetches = totalLongRunningOutstandingFetches.get(host) || 0;
                assert(fetches > 0);
                if (fetches === 1) {
                    totalLongRunningOutstandingFetches.delete(host);
                }
                else {
                    totalLongRunningOutstandingFetches.set(host, fetches - 1);
                }
                if (fetches - 1 < totalLongRunningOutstandingFetchesThreshold) {
                    haveConsoleWarned.delete(host);
                }
            }
            const fetches = totalOutstandingFetches.get(host) || 0;
            assert(fetches > 0);
            if (fetches === 1) {
                totalOutstandingFetches.delete(host);
            }
            else {
                totalOutstandingFetches.set(host, fetches - 1);
            }
        },
    };
}
const warnings = {};
const pendingWarnings = {};
function renderWarnings() {
    const html = document.documentElement;
    // Remove previously rendered warnings so that we compute the proper
    // offsets for layout.
    //
    // TODO(benh): put all of these in a containing `div` so we can just
    // remove it instead?
    for (const warning in warnings) {
        const warningElement = document.getElementById(warning);
        if (warningElement !== null) {
            html.removeChild(warningElement);
        }
    }
    let warningsShown = 0;
    for (const warning in warnings) {
        const warningElement = document.createElement("div");
        warningElement.setAttribute("id", warning);
        // Positioning: left-top corner but place each warning to the right of the previous.
        const messageWidth = 300;
        const leftOffset = warningsShown * messageWidth;
        // More positioning: add a little margin, hover above ~everything (z-index).
        // Looks: red border, white background.
        // Content: text, slightly padded.
        warningElement.style.cssText = `position:absolute;top:0px;left:${leftOffset}px;margin:4px;width:500px;z-index:100000;
    border:3px solid red;border-radius:4px;background-color:white;
    padding:4px;font-family:sans-serif`;
        warningElement.innerHTML = "⚠ " + warnings[warning];
        html.appendChild(warningElement);
        warningsShown++;
    }
}
function scheduleWarning({ messageHtml, id, delay_secs, }) {
    if (id in pendingWarnings) {
        return;
    }
    pendingWarnings[id] = setTimeout(() => {
        addWarning({ messageHtml, id });
        delete pendingWarnings[id];
    }, delay_secs * 1000);
}
function addWarning({ messageHtml, id }) {
    console.warn(messageHtml);
    warnings[id] = messageHtml;
    renderWarnings();
}
function removeWarning({ id }) {
    if (id in pendingWarnings) {
        clearTimeout(pendingWarnings[id]);
        delete pendingWarnings[id];
    }
    if (id in warnings) {
        delete warnings[id];
        const warningElement = document.getElementById(id);
        if (warningElement !== null) {
            const html = document.documentElement;
            html.removeChild(warningElement);
        }
        renderWarnings();
    }
}
function maybeScheduleLocalhostWarning({ id, url, }) {
    if (
    // Only show the warning for localhost (development).
    !(url.includes("localhost") || url.includes("127.0.0.1")) ||
        // Only show the warning if it's not already scheduled or active.
        id in pendingWarnings ||
        id in warnings) {
        return;
    }
    const endpoint = url.split("/")[2]; // e.g. "localhost:9991"
    const warningText = `<b>Trouble connecting...</b><br/>
  <br/>
  Looks like we couldn't connect to '${endpoint}'. This may be due to one
  of the following reasons:<br/>
  <li>Your backend application is not running.</li>
  <li>Your backend application is running, but not on 127.0.0.1.</li>
  <li>Your backend application is running, but on a different port.</li>
  <br/>
  (This message will only appear during local development)
  `;
    scheduleWarning({
        messageHtml: warningText,
        id: id,
        // User feedback says that showing the a warning message immediately on
        // connection errors is too aggressive - for example, Chaos Monkey may
        // be restarting the application, and everything is fine. However, if
        // after 10 seconds we still can't talk to the application, then we can
        // assume that something is wrong.
        delay_secs: 10,
    });
}
export function guardedFetch(request, options) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        // If not in development mode, just fetch.
        if (process.env.NODE_ENV !== "development") {
            return fetch(request, options);
        }
        const { completeFetch } = trackFetch(new URL(request.url));
        try {
            const response = yield fetch(request, options);
            // If no exception was thrown while doing a fetch, then we must be able to
            // reach the server, so if we previously had displayed a warning stop
            // displaying it now.
            //
            // Likely what has happened is the server we are trying to fetch from
            // restarted.
            removeWarning({ id: "guardedFetch" });
            return response;
        }
        catch (error) {
            if (!((_a = options === null || options === void 0 ? void 0 : options.signal) === null || _a === void 0 ? void 0 : _a.aborted)) {
                // The fetch failed due to some network error. Tell the developer, if this
                // situation persists.
                maybeScheduleLocalhostWarning({ id: "guardedFetch", url: request.url });
            }
            throw error;
        }
        finally {
            completeFetch();
        }
    });
}
export class WebContext {
    constructor(args) {
        if (typeof args === "string") {
            this.url = args;
            this.bearerToken = undefined;
        }
        else {
            this.url = args.url;
            this.bearerToken =
                args.bearerToken !== undefined
                    ? typeof args.bearerToken === "string"
                        ? () => __awaiter(this, void 0, void 0, function* () { return args.bearerToken; })
                        : args.bearerToken
                    : undefined;
        }
    }
    setBearerToken(bearerToken) {
        if (typeof bearerToken === "string") {
            // Convert string tokens to async functions for backward
            // compatibility.
            this.bearerToken = () => __awaiter(this, void 0, void 0, function* () { return bearerToken; });
        }
        else {
            this.bearerToken = bearerToken;
        }
    }
}
